import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import FetchData from "../components/FetchData";
import SnowzoneComponent from "../components/Snowzone";
import ShortSnowbarrierComponent from "../components/Snowbarrier-short";
import MeasurementWrapper from "../components/MeasurementWrapper";
import {
  StyledContent,
  StyledElement,
  StyledSelectWrapper,
  StyledWrapper,
} from "../styles/styledComponents";
import LangParam from "../types/LangParam";
import RadioSelectWrapper from "../components/RadioSelectWrapper";
import ProductCategory from "../types/ProductCategory";
import MountingType from "../types/MountingType";
import ColorSelectWrapper from "../components/ColorSelectWrapper";
import Color from "../types/Color";
import MountingTypeField from "../types/MountingTypeField";
import SelectedMountingType from "../types/SelectedMountingType";
import SelectedProduct from "../types/SelectedProduct";
import SelectedColor from "../types/SelectedColor";
import { useSelectedContextState } from "../context/selectedContext";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import { scroller } from "react-scroll";
import SummaryTable from "../components/SummaryTable";
import Footer from "../components/Footer";
import { Element } from "react-scroll";
import { MountingTypeCode } from "../types/MountingTypeCode";

type HomeProps = {
  setParentLanguage: (value: any) => any;
  theme?: any;
};

const Home = (props: HomeProps) => {
  let { languageId = "SE" }: LangParam = useParams();
  let { setParentLanguage, theme } = props;

  const { t } = useTranslation("trans");
  const [state, dispatch] = useSelectedContextState();
  const { clearAll, clearChildrenStates } = state;
  const [selectedMountingType, setSelectedMountingType] = useState<
    SelectedMountingType | undefined
  >();
  const [selectedProduct, setSelectedProduct] = useState<
    SelectedProduct | undefined
  >();
  const [selectedColor, setSelectedColor] = useState<
    SelectedColor | undefined
  >();

  const [getProductsResult, setProductsResult] = useState<ProductCategory[]>(
    []
  );
  const [getMountingTypeResult, setMountingTypeResult] =
    useState<MountingType[]>();
  const [getColorsResult, setColorsResult] = useState<Color[]>();
  const [getMountingTypeFieldResult, setMountingTypeFieldResult] =
    useState<MountingTypeField[]>();

  const [hideMounted, setHideMounted] = useState(false);
  const [hideSnowzone, setHideSnowzone] = useState(true);

  const scrollTo = (name: any) => {
    scroller.scrollTo(name, {
      duration: 800,
      delay: 0,
      smooth: "easeInOutQuart",
    });
  };

  const onSelectedMountingType = async (value: string) => {
    let mouningType = getMountingTypeResult?.find(
      (x) => x.id.toString() === value
    );

    let selected: SelectedMountingType = {
      id: Number(value),
      code: mouningType?.code,
      name: mouningType?.name,
    };
    setSelectedMountingType(selected);
  };

  const onSelectedProduct = async (value: string) => {
    let product = getProductsResult?.find((x) => x.id.toString() === value);

    let selected: SelectedProduct = {
      id: Number(value),
      name: product?.name || "",
      enableSnowLoadZone: product?.enableSnowLoadZone || false,
      showCcTable: product?.showCcTable || false,
    };

    setHideSnowzone(true);

    if (selected.enableSnowLoadZone) {
      setHideSnowzone(false);
    }

    setSelectedProduct(selected);
  };

  const onSelectedColor = async (value: string) => {
    let name = getColorsResult?.find((x) => x.id.toString() === value)?.name;

    let selected: SelectedColor = { id: Number(value), name: name || "" };
    setSelectedColor(selected);
  };

  const onMountedTypeResult = async (value: MountingType[]) => {
    let visibleMountingTypes = value.filter((x) => x.visible !== false);
    setMountingTypeResult(visibleMountingTypes);

    if (visibleMountingTypes.length <= 1) {
      setHideMounted(true);
      // Auto-select the single visible type; if none are visible, fall back to
      // the first type so color/measurement/calculation can still proceed.
      setSelectedMountingType(visibleMountingTypes[0] ?? value[0]);
    } else {
      setHideMounted(false);
    }
  };

  const clearChildren = useCallback(() => {
    setSelectedMountingType(undefined);
    setSelectedColor(undefined);
    setColorsResult([]);
    setMountingTypeResult([]);
    setMountingTypeFieldResult([]);
  }, []);

  useEffect(() => {
    if (clearChildrenStates) {
      clearChildren();
      dispatch({ type: "setClearChildrenStates", payload: false });
    }
  }, [clearChildren, clearChildrenStates, dispatch]);

  useEffect(() => {
    if (clearAll) {
      scrollTo("section1");
      setSelectedProduct(undefined);
      clearChildren();
      setHideSnowzone(true);
      setHideMounted(true);
      dispatch({ type: "setClearAll", payload: false });
    }
  }, [clearAll, clearChildren, dispatch]);

  useEffect(() => {
    i18n.changeLanguage(languageId);
  }, [languageId]);

  useEffect(() => {
    setParentLanguage(languageId);
    document.title = t("application.title");
  }, [languageId, setParentLanguage]);

  return (
    <StyledWrapper lang={languageId}>
      <Sidebar
        hideMounted={hideMounted}
        productSectionDone={selectedProduct !== undefined}
        mountedSectionDone={selectedMountingType !== undefined}
        colorSectionDone={selectedColor !== undefined}
      />
      <StyledContent>
        <FetchData
          selectedColorId={selectedColor?.id}
          selectedMountingTypeId={selectedMountingType?.id}
          selectedProductId={selectedProduct?.id}
          products={setProductsResult}
          mountingTypes={onMountedTypeResult}
          colors={setColorsResult}
          fields={setMountingTypeFieldResult}
        />

        <StyledElement name="section1" className="element">
          <RadioSelectWrapper
            type="product"
            setFirstDefaultValue={false}
            selected={(value) => {
              onSelectedProduct(value);
            }}
            header={t("selectTitles.product")}
            radioValues={getProductsResult || []}
            clickNext={() => scrollTo("section2")}
          />
        </StyledElement>
        {!hideMounted && (
          <StyledElement name="section2" className="element">
            <RadioSelectWrapper
              type="mount"
              selected={(value) => onSelectedMountingType(value)}
              header={t("selectTitles.mounted")}
              radioValues={
                getMountingTypeResult?.sort(
                  (a, b) => a.sortOrder - b.sortOrder
                ) || []
              }
              clickNext={() => scrollTo("section3")}
            />
          </StyledElement>
        )}
        <StyledElement name="section3" className="element">
          <ColorSelectWrapper
            selected={(value) => {
              onSelectedColor(value);
            }}
            colorItems={getColorsResult || []}
            clickNext={() => scrollTo("section4")}
            countryCode={languageId}
          />
        </StyledElement>

        {!hideSnowzone && (
          <Element name="snowzone" className="snowzone-container">
            <SnowzoneComponent countryCode={languageId}></SnowzoneComponent>
          </Element>
        )}

        {!hideSnowzone && (
          <Element
            name="snowbarrier-short"
            className="short-snowbarrier-container"
          >
            <ShortSnowbarrierComponent></ShortSnowbarrierComponent>
          </Element>
        )}

        <Element name="section4" className="element">
          <div style={{ display: "flex" }}>
            <MeasurementWrapper
              header={t("selectTitles.measure")}
              mountingTypeFields={
                getMountingTypeFieldResult?.sort(
                  (a, b) => a.sortOrder - b.sortOrder
                ) || []
              }
              color={selectedColor}
              hideMounted={hideMounted}
              mountingType={selectedMountingType}
              product={selectedProduct}
              countryCode={languageId}
            />
          </div>
          <SummaryTable countryCode={languageId} />

          <Footer theme={theme} />
        </Element>
      </StyledContent>
    </StyledWrapper>
  );
};

export default Home;
